// Utility for printing & WhatsApp-sharing a bill
// - POS retail: 80mm thermal receipt
// - Wholesale: A4 invoice

const LOGO_URL = `${window.location.origin}/eeman-logo.jpg`;

const escapeHtml = (str) =>
  String(str || "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;");

const fmtINR = (n) =>
  new Intl.NumberFormat("en-IN", {
    style: "currency",
    currency: "INR",
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(Math.round(Number(n || 0)));

const fmtKg = (n) =>
  Number(n || 0).toLocaleString("en-IN", { maximumFractionDigits: 3 });

const fmtDate = (iso) => {
  if (!iso || typeof iso !== "string") return "";
  const parts = iso.slice(0, 10).split("-");
  if (parts.length !== 3) return iso;
  const [y, m, d] = parts;
  return `${d}-${m}-${y.slice(2)}`;
};

const fmtQty = (it) => {
  if (it.unit === "piece") {
    const n = Math.round(Number(it.kg || 0));
    return `${n} ${n === 1 ? "pc" : "pcs"}`;
  }
  return `${fmtKg(it.kg)} kg`;
};

/* ---------- 80mm Thermal Receipt (POS) ---------- */
function buildReceiptHTML({ sale, businessName = "EEMAN MEATS" }) {
  const billNo = sale.bill_no ? String(sale.bill_no) : sale.id ? sale.id.slice(0, 8).toUpperCase() : "—";
  const rows = sale.items
    .map(
      (it) => `
        <tr>
          <td colspan="3" style="padding-top:4px">${escapeHtml(it.product_name)}</td>
        </tr>
        <tr>
          <td>${fmtQty(it)}</td>
          <td class="right">× ₹${fmtKg(it.rate)}</td>
          <td class="right">${fmtINR(it.amount)}</td>
        </tr>`
    )
    .join("");

  const totalKg = sale.items
    .filter((it) => (it.unit || "kg") !== "piece")
    .reduce((s, it) => s + Number(it.kg || 0), 0);
  const totalPcs = sale.items
    .filter((it) => it.unit === "piece")
    .reduce((s, it) => s + Number(it.kg || 0), 0);
  const qtyLine = [];
  if (totalKg > 0) qtyLine.push(`${fmtKg(totalKg)} kg`);
  if (totalPcs > 0) qtyLine.push(`${Math.round(totalPcs)} pcs`);
  const qtySummary = qtyLine.join(" + ") || "0";

  return `<!doctype html>
<html><head><meta charset="utf-8"/>
<title>BILL #${billNo}</title>
<style>
  @page { size: 80mm auto; margin: 3mm; }
  * { box-sizing: border-box; }
  body { font-family: 'Courier New', monospace; margin: 0; padding: 10px; color: #000; font-size: 12px; line-height: 1.4; max-width: 80mm; }
  .head { text-align: center; border-bottom: 2px dashed #000; padding-bottom: 6px; margin-bottom: 8px; }
  .head h1 { margin: 0; font-size: 18px; letter-spacing: 0.1em; }
  .head .sub { font-size: 10px; letter-spacing: 0.2em; text-transform: uppercase; }
  .billno { font-size: 26px; font-weight: 900; line-height: 1.1; }
  .meta { display: flex; justify-content: space-between; align-items: flex-end; margin-bottom: 8px; font-size: 11px; }
  table { width: 100%; border-collapse: collapse; }
  td { padding: 1px 0; font-size: 11px; }
  .right { text-align: right; }
  .totals { margin-top: 6px; display: flex; justify-content: space-between; font-size: 12px; padding-top: 4px; border-top: 1px dashed #000; }
  .grand { font-size: 16px; font-weight: bold; border-top: 2px solid #000; padding-top: 4px; margin-top: 4px; }
  .foot { margin-top: 12px; text-align: center; font-size: 10px; border-top: 1px dashed #000; padding-top: 6px; }
  .actions { margin-top: 16px; padding-top: 12px; border-top: 1px solid #ccc; display: flex; gap: 6px; }
  .btn { flex: 1; padding: 8px; border: 1px solid #000; background: #fff; cursor: pointer; font-family: inherit; font-size: 11px; text-transform: uppercase; letter-spacing: 0.05em; }
  .btn-primary { background: #000; color: #fff; }
  @media print { .actions { display: none; } body { padding: 0; max-width: none; } }
</style></head>
<body>
  <div class="head">
    <img src="${LOGO_URL}" alt="${escapeHtml(businessName)}" style="max-height:50px;max-width:200px;object-fit:contain;display:block;margin:0 auto 4px;" onerror="this.style.display='none'"/>
    <h1>${escapeHtml(businessName)}</h1>
    <div class="sub">Retail Bill</div>
  </div>
  <div class="meta">
    <div>
      <div style="font-size:9px;text-transform:uppercase;letter-spacing:0.1em;">Bill No</div>
      <div class="billno">${billNo}</div>
    </div>
    <div>${escapeHtml(fmtDate(sale.date))}</div>
  </div>
  ${
    sale.customer_name
      ? `<div style="margin-bottom:6px;font-size:11px;">${escapeHtml(sale.customer_name)}</div>`
      : ""
  }
  <table>${rows}</table>
  <div class="totals"><span>Total Kg</span><span>${fmtKg(sale.total_kg)} kg</span></div>
  <div class="totals grand"><span>TOTAL</span><span>${fmtINR(sale.total_amount)}</span></div>
  ${
    sale.paid_amount > 0
      ? `<div class="totals"><span>Paid</span><span>${fmtINR(sale.paid_amount)}</span></div>
         ${
           sale.paid_amount < sale.total_amount
             ? `<div class="totals"><span>Balance</span><span>${fmtINR(sale.total_amount - sale.paid_amount)}</span></div>`
             : ""
         }`
      : ""
  }
  <div class="foot">Thank you · visit again</div>
  <div class="actions">
    <button class="btn" onclick="window.close()">Close</button>
    <button class="btn btn-primary" onclick="window.print()">Print</button>
  </div>
</body></html>`;
}

/* ---------- A4 Wholesale Invoice ---------- */
function buildInvoiceHTML({ sale, businessName = "EEMAN MEATS", customer = null, docLabel = "Wholesale Invoice" }) {
  const invNo = sale.bill_no ? String(sale.bill_no) : sale.id ? sale.id.slice(0, 8).toUpperCase() : "—";
  const rows = sale.items
    .map(
      (it, i) => `
        <tr>
          <td style="text-align:center;">${i + 1}</td>
          <td>${escapeHtml(it.product_name)}</td>
          <td style="text-align:right;">${fmtKg(it.kg)}</td>
          <td style="text-align:right;">₹${fmtKg(it.rate)}</td>
          <td style="text-align:right;">${fmtINR(it.amount)}</td>
        </tr>`
    )
    .join("");

  const balance = (sale.total_amount || 0) - (sale.paid_amount || 0);
  const totalKg = sale.items
    .filter((it) => (it.unit || "kg") !== "piece")
    .reduce((s, it) => s + Number(it.kg || 0), 0);
  const totalPcs = sale.items
    .filter((it) => it.unit === "piece")
    .reduce((s, it) => s + Number(it.kg || 0), 0);
  const qtyLine = [];
  if (totalKg > 0) qtyLine.push(`${fmtKg(totalKg)} kg`);
  if (totalPcs > 0) qtyLine.push(`${Math.round(totalPcs)} pcs`);
  const qtySummary = qtyLine.join(" + ") || "0";

  return `<!doctype html>
<html><head><meta charset="utf-8"/>
<title>INVOICE ${invNo}</title>
<style>
  @page { size: A4; margin: 14mm; }
  * { box-sizing: border-box; }
  body { font-family: 'Helvetica', 'Arial', sans-serif; margin: 0; color: #000; font-size: 12px; line-height: 1.5; }
  .doc { max-width: 210mm; margin: 0 auto; padding: 20px; }
  .head { display: flex; justify-content: space-between; align-items: flex-start; border-bottom: 3px solid #000; padding-bottom: 14px; margin-bottom: 18px; }
  .brand { font-family: 'Georgia', serif; font-size: 28px; font-weight: bold; letter-spacing: 0.05em; }
  .sub { font-size: 10px; letter-spacing: 0.2em; text-transform: uppercase; color: #555; margin-top: 2px; }
  .invoice-title { text-align: right; }
  .invoice-title h2 { margin: 0; font-size: 22px; letter-spacing: 0.1em; }
  .invoice-title .meta-line { font-size: 11px; color: #444; margin-top: 4px; }
  .parties { display: grid; grid-template-columns: 1fr 1fr; gap: 20px; margin-bottom: 18px; }
  .party-label { font-size: 9px; text-transform: uppercase; letter-spacing: 0.15em; color: #777; margin-bottom: 4px; }
  .party-name { font-weight: bold; font-size: 14px; }
  .party-detail { font-size: 11px; color: #333; margin-top: 2px; }
  table.items { width: 100%; border-collapse: collapse; margin-bottom: 14px; }
  table.items th { background: #18181b; color: #fff; text-align: left; padding: 8px 10px; font-size: 10px; text-transform: uppercase; letter-spacing: 0.1em; }
  table.items th:first-child { width: 36px; text-align: center; }
  table.items td { padding: 8px 10px; border-bottom: 1px solid #e4e4e7; font-size: 12px; }
  table.items tr:last-child td { border-bottom: 2px solid #000; }
  .totals-block { display: flex; justify-content: flex-end; margin-bottom: 20px; }
  .totals-table { width: 280px; border-collapse: collapse; }
  .totals-table td { padding: 6px 10px; font-size: 12px; }
  .totals-table .label { color: #555; }
  .totals-table .value { text-align: right; font-family: 'Courier New', monospace; }
  .totals-table .grand td { border-top: 2px solid #000; padding-top: 8px; font-size: 16px; font-weight: bold; }
  .totals-table .balance td { color: #b91c1c; font-weight: bold; }
  .notes { border-top: 1px solid #e4e4e7; padding-top: 12px; font-size: 11px; color: #555; }
  .signature { display: flex; justify-content: space-between; margin-top: 50px; }
  .sig-line { width: 200px; border-top: 1px solid #000; padding-top: 4px; text-align: center; font-size: 10px; text-transform: uppercase; letter-spacing: 0.1em; }
  .actions { margin-top: 30px; padding-top: 20px; border-top: 1px solid #ccc; display: flex; gap: 10px; justify-content: center; }
  .btn { padding: 10px 24px; border: 1px solid #000; background: #fff; cursor: pointer; font-family: inherit; font-size: 12px; text-transform: uppercase; letter-spacing: 0.1em; }
  .btn-primary { background: #000; color: #fff; }
  @media print { .actions { display: none; } .doc { padding: 0; } }
</style></head>
<body>
<div class="doc">
  <div class="head">
    <div style="display:flex;align-items:center;gap:14px;">
      <img src="${LOGO_URL}" alt="${escapeHtml(businessName)}" style="height:60px;width:auto;object-fit:contain;" onerror="this.style.display='none'"/>
      <div>
        <div class="brand">${escapeHtml(businessName)}</div>
        <div class="sub">${escapeHtml(docLabel)}</div>
      </div>
    </div>
    <div class="invoice-title">
      <h2>INVOICE</h2>
      <div class="meta-line">Invoice #: <b>${invNo}</b></div>
      <div class="meta-line">Date: <b>${escapeHtml(fmtDate(sale.date))}</b></div>
    </div>
  </div>

  <div class="parties">
    <div>
      <div class="party-label">Bill To</div>
      <div class="party-name">${escapeHtml(sale.customer_name || (customer && customer.name) || "—")}</div>
      ${customer && customer.phone ? `<div class="party-detail">${escapeHtml(customer.phone)}</div>` : ""}
      ${customer && customer.address ? `<div class="party-detail">${escapeHtml(customer.address)}</div>` : ""}
    </div>
    <div style="text-align:right">
      <div class="party-label">Payment</div>
      <div class="party-name">${escapeHtml((sale.payment_mode || "Credit").toUpperCase())}</div>
      ${sale.delivery_date ? `<div class="party-detail">Delivery: ${escapeHtml(fmtDate(sale.delivery_date))}</div>` : ""}
      ${sale.paid_amount > 0 ? `<div class="party-detail">${sale.is_party ? "Advance" : "Paid"} ${fmtINR(sale.paid_amount)}</div>` : ""}
    </div>
  </div>

  <table class="items">
    <thead>
      <tr>
        <th>#</th>
        <th>Product</th>
        <th style="text-align:right">Qty (kg)</th>
        <th style="text-align:right">Rate</th>
        <th style="text-align:right">Amount</th>
      </tr>
    </thead>
    <tbody>${rows}</tbody>
  </table>

  <div class="totals-block">
    <table class="totals-table">
      <tr><td class="label">Total Quantity</td><td class="value">${qtySummary}</td></tr>
      <tr><td class="label">Subtotal</td><td class="value">${fmtINR(sale.total_amount)}</td></tr>
      <tr class="grand"><td>Grand Total</td><td class="value">${fmtINR(sale.total_amount)}</td></tr>
      ${sale.paid_amount > 0 ? `<tr><td class="label">${sale.is_party ? "Advance Paid" : "Paid"}</td><td class="value">${fmtINR(sale.paid_amount)}</td></tr>` : ""}
      ${balance > 0 ? `<tr class="balance"><td>Balance Due</td><td class="value">${fmtINR(balance)}</td></tr>` : ""}
    </table>
  </div>

  ${sale.notes ? `<div class="notes"><b>Notes:</b> ${escapeHtml(sale.notes)}</div>` : ""}

  <div class="signature">
    <div class="sig-line">Customer Signature</div>
    <div class="sig-line">Authorised Signatory</div>
  </div>

  <div class="actions">
    <button class="btn" onclick="window.close()">Close</button>
    <button class="btn btn-primary" onclick="window.print()">Print</button>
  </div>
</div>
</body></html>`;
}

export function buildBillHTML({ sale, businessName = "EEMAN MEATS", customer = null, docLabel }) {
  if (sale.channel === "wholesale") {
    return buildInvoiceHTML({
      sale,
      businessName,
      customer,
      docLabel: docLabel || (sale.is_party ? "Party Order Invoice" : "Wholesale Invoice"),
    });
  }
  return buildReceiptHTML({ sale, businessName });
}

export function openBillWindow(sale, options = {}) {
  const isWholesale = sale.channel === "wholesale";
  const width = isWholesale ? 820 : 420;
  const height = 800;
  const w = window.open("", "_blank", `width=${width},height=${height}`);
  if (!w) return;
  w.document.write(buildBillHTML({ sale, ...options }));
  w.document.close();
}

export function shareBillWhatsApp(sale, { phone = "", businessName = "EEMAN MEATS", label = "" } = {}) {
  const billNo = sale.bill_no ? String(sale.bill_no) : sale.id ? sale.id.slice(0, 8).toUpperCase() : "";
  const lines = [
    `*${businessName}* — ${label || (sale.is_party ? "PARTY ORDER" : sale.channel === "wholesale" ? "INVOICE" : "BILL")} #${billNo}`,
    `Date: ${fmtDate(sale.date)}`,
    sale.delivery_date ? `Delivery: ${fmtDate(sale.delivery_date)}` : "",
    sale.customer_name ? `Customer: ${sale.customer_name}` : "",
    "",
    ...sale.items.map(
      (it) => {
        const q = it.unit === "piece"
          ? `${Math.round(Number(it.kg || 0))} pcs`
          : `${fmtKg(it.kg)} kg`;
        return `• ${it.product_name} — ${q} × ₹${fmtKg(it.rate)} = ${fmtINR(it.amount)}`;
      }
    ),
    "",
    `Total: *${fmtINR(sale.total_amount)}*`,
    sale.paid_amount > 0 ? `${sale.is_party ? "Advance Paid" : "Paid"}: ${fmtINR(sale.paid_amount)}` : "",
    sale.paid_amount > 0 && sale.paid_amount < sale.total_amount
      ? `Balance: ${fmtINR(sale.total_amount - sale.paid_amount)}`
      : "",
    "",
    "Thank you 🙏",
  ]
    .filter(Boolean)
    .join("\n");
  const text = encodeURIComponent(lines);
  const url = phone
    ? `https://wa.me/${phone.replace(/[^\d]/g, "")}?text=${text}`
    : `https://wa.me/?text=${text}`;
  window.open(url, "_blank");
}

/* ---------- Consolidated Party Bill ---------- */
function buildConsolidatedHTML(data, { businessName = "EEMAN MEATS", companyPhone = "" } = {}) {
  const orderRows = data.orders
    .map(
      (o) => `
        <tr>
          <td>${escapeHtml(fmtDate(o.date))}</td>
          <td style="text-align:center;">#${o.order_no}</td>
          <td>${escapeHtml(o.items.map((i) => i.product_name).join(", "))}</td>
          <td style="text-align:right;">${fmtINR(o.total_amount)}</td>
          <td style="text-align:right;">${fmtINR(o.paid_amount)}</td>
        </tr>`
    )
    .join("");

  const itemRows = data.items
    .map(
      (it) => `
        <tr>
          <td>${escapeHtml(it.product_name)}</td>
          <td style="text-align:right;">${it.unit === "piece" ? Math.round(it.qty) + " pcs" : fmtKg(it.qty) + " kg"}</td>
          <td style="text-align:right;">₹${fmtKg(it.rate)}</td>
          <td style="text-align:right;">${fmtINR(it.amount)}</td>
        </tr>`
    )
    .join("");

  return `<!doctype html>
<html><head><meta charset="utf-8"/>
<title>CONSOLIDATED BILL — ${escapeHtml(data.customer_name)}</title>
<style>
  @page { size: A4; margin: 14mm; }
  body { font-family: 'Helvetica', 'Arial', sans-serif; margin: 0; color: #000; font-size: 12px; line-height: 1.5; }
  .doc { max-width: 210mm; margin: 0 auto; padding: 20px; }
  .head { display: flex; justify-content: space-between; align-items: flex-start; border-bottom: 3px solid #000; padding-bottom: 14px; margin-bottom: 18px; }
  .brand { font-family: 'Georgia', serif; font-size: 26px; font-weight: bold; }
  .sub { font-size: 10px; letter-spacing: 0.2em; text-transform: uppercase; color: #555; }
  h3 { font-size: 12px; text-transform: uppercase; letter-spacing: 0.1em; margin: 18px 0 6px; }
  table { width: 100%; border-collapse: collapse; }
  th { background: #18181b; color: #fff; text-align: left; padding: 6px 8px; font-size: 10px; text-transform: uppercase; }
  td { padding: 6px 8px; border-bottom: 1px solid #e4e4e7; font-size: 11px; }
  .totals { width: 300px; margin-left: auto; margin-top: 14px; }
  .totals td { font-size: 12px; }
  .totals .grand td { border-top: 2px solid #000; font-size: 16px; font-weight: bold; padding-top: 8px; color: #b91c1c; }
  .foot { margin-top: 30px; border-top: 1px solid #ccc; padding-top: 8px; font-size: 10px; color: #555; text-align: center; }
  .actions { margin-top: 24px; display: flex; gap: 10px; justify-content: center; }
  .btn { padding: 10px 24px; border: 1px solid #000; background: #fff; cursor: pointer; font-size: 12px; text-transform: uppercase; }
  .btn-primary { background: #000; color: #fff; }
  @media print { .actions { display: none; } .doc { padding: 0; } }
</style></head>
<body>
<div class="doc">
  <div class="head">
    <div style="display:flex;align-items:center;gap:14px;">
      <img src="${LOGO_URL}" style="height:56px;object-fit:contain;" onerror="this.style.display='none'"/>
      <div>
        <div class="brand">${escapeHtml(businessName)}</div>
        <div class="sub">Consolidated Bill</div>
      </div>
    </div>
    <div style="text-align:right">
      <div style="font-size:14px;font-weight:bold;">${escapeHtml(data.customer_name)}</div>
      ${data.phone ? `<div style="font-size:11px;color:#444;">${escapeHtml(data.phone)}</div>` : ""}
      <div style="font-size:11px;color:#444;margin-top:4px;">Period: <b>${escapeHtml(fmtDate(data.from))} → ${escapeHtml(fmtDate(data.to))}</b></div>
    </div>
  </div>

  <h3>Orders (${data.order_count})</h3>
  <table>
    <thead><tr><th>Date</th><th style="text-align:center;">Order</th><th>Items</th><th style="text-align:right;">Amount</th><th style="text-align:right;">Paid</th></tr></thead>
    <tbody>${orderRows || '<tr><td colspan="5" style="text-align:center;color:#777;">No orders in this period</td></tr>'}</tbody>
  </table>

  ${itemRows ? `<h3>Combined Items</h3>
  <table>
    <thead><tr><th>Product</th><th style="text-align:right;">Qty</th><th style="text-align:right;">Rate</th><th style="text-align:right;">Amount</th></tr></thead>
    <tbody>${itemRows}</tbody>
  </table>` : ""}

  <table class="totals">
    <tr><td>Period Total</td><td style="text-align:right;font-family:monospace;">${fmtINR(data.period_total)}</td></tr>
    ${data.period_paid > 0 ? `<tr><td>Paid</td><td style="text-align:right;font-family:monospace;">− ${fmtINR(data.period_paid)}</td></tr>` : ""}
    ${data.old_balance > 0 ? `<tr><td>Previous Balance</td><td style="text-align:right;font-family:monospace;">+ ${fmtINR(data.old_balance)}</td></tr>` : ""}
    <tr class="grand"><td>TOTAL DUE</td><td style="text-align:right;font-family:monospace;">${fmtINR(data.grand_due)}</td></tr>
  </table>

  <div class="foot">${escapeHtml(businessName)}${companyPhone ? ` · ${escapeHtml(companyPhone)}` : ""} — Thank you for your business</div>
  <div class="actions">
    <button class="btn" onclick="window.close()">Close</button>
    <button class="btn btn-primary" onclick="window.print()">Print</button>
  </div>
</div>
</body></html>`;
}

export function openConsolidatedWindow(data, opts = {}) {
  const w = window.open("", "_blank", "width=820,height=800");
  if (!w) return;
  w.document.write(buildConsolidatedHTML(data, opts));
  w.document.close();
}

export function shareConsolidatedWhatsApp(data, { phone = "", businessName = "EEMAN MEATS", companyPhone = "" } = {}) {
  const lines = [
    `*${businessName}* — CONSOLIDATED BILL`,
    `Customer: ${data.customer_name}`,
    `Period: ${fmtDate(data.from)} to ${fmtDate(data.to)}`,
    "",
    ...data.orders.map((o) => `• ${fmtDate(o.date)} (Order #${o.order_no}) — ${fmtINR(o.total_amount)}`),
    "",
    `Period Total: ${fmtINR(data.period_total)}`,
    data.period_paid > 0 ? `Paid: ${fmtINR(data.period_paid)}` : "",
    data.old_balance > 0 ? `Previous Balance: ${fmtINR(data.old_balance)}` : "",
    `*TOTAL DUE: ${fmtINR(data.grand_due)}*`,
    "",
    `${businessName}${companyPhone ? ` · ${companyPhone}` : ""}`,
    "Thank you 🙏",
  ]
    .filter(Boolean)
    .join("\n");
  const text = encodeURIComponent(lines);
  const url = phone
    ? `https://wa.me/${phone.replace(/[^\d]/g, "")}?text=${text}`
    : `https://wa.me/?text=${text}`;
  window.open(url, "_blank");
}

/* ---------- Supplier Statement (Payables receipt) ---------- */
const purchaseCategory = (t) => (t === "live_chicken" ? "chicken" : "mutton");

export function openSupplierStatementWindow(ledger, category = "all", { businessName = "EEMAN MEATS", companyPhone = "9025811576" } = {}) {
  const purchases = (ledger.purchases || []).filter(
    (p) => category === "all" || purchaseCategory(p.type) === category
  );
  const billed = purchases.reduce((s, p) => s + (p.total_amount || 0), 0);
  const paid = purchases.reduce((s, p) => s + (p.paid_amount || 0), 0);
  const payments = category === "all" ? ledger.payments || [] : [];
  const extraPaid = payments.reduce((s, p) => s + (p.amount || 0), 0);
  const balance = category === "all" ? ledger.outstanding : billed - paid;
  const catLabel = category === "all" ? "All Purchases" : category === "chicken" ? "Chicken Purchases" : "Mutton Purchases";

  const rows = purchases
    .map((p) => {
      const head = `<tr style="background:#f4f4f5;">
        <td style="font-weight:bold;">${escapeHtml(fmtDate(p.date))}</td>
        <td style="text-transform:capitalize;font-weight:bold;">${escapeHtml((p.type || "").replace(/_/g, " "))}</td>
        <td style="text-align:right;font-weight:bold;">${fmtKg(p.total_kg)} kg</td>
        <td></td>
        <td style="text-align:right;font-weight:bold;">${fmtINR(p.total_amount)}</td>
        <td style="text-align:right;">${fmtINR(p.paid_amount)}</td>
        <td style="text-align:center;text-transform:uppercase;font-size:9px;">${escapeHtml(p.payment_status || "")}</td>
      </tr>`;
      const itemRows = (p.items || [])
        .map(
          (it) => `<tr>
        <td></td>
        <td style="color:#555;padding-left:18px;">↳ ${escapeHtml(it.label || "")}</td>
        <td style="text-align:right;">${(it.unit || "kg") === "piece" ? Math.round(it.kg) + " pcs" : fmtKg(it.kg) + " kg"}</td>
        <td style="text-align:right;font-family:monospace;">₹${fmtKg(it.price_per_kg)}${(it.unit || "kg") === "piece" ? "/pc" : "/kg"}</td>
        <td style="text-align:right;">${fmtINR(it.amount)}</td>
        <td></td>
        <td></td>
      </tr>`
        )
        .join("");
      return head + itemRows;
    })
    .join("");

  const payRows = payments
    .map(
      (p) => `<tr>
        <td>${escapeHtml(fmtDate(p.date))}</td>
        <td style="text-transform:uppercase;">${escapeHtml(p.mode || "")}</td>
        <td>${escapeHtml(p.notes || "-")}</td>
        <td style="text-align:right;">${fmtINR(p.amount)}</td>
      </tr>`
    )
    .join("");

  const html = `<!doctype html>
<html><head><meta charset="utf-8"/>
<title>Supplier Statement — ${escapeHtml(ledger.supplier)}</title>
<style>
  @page { size: A4; margin: 14mm; }
  body { font-family: 'Helvetica', 'Arial', sans-serif; margin: 0; color: #000; font-size: 12px; line-height: 1.5; }
  .doc { max-width: 210mm; margin: 0 auto; padding: 20px; }
  .head { display: flex; justify-content: space-between; align-items: flex-start; border-bottom: 3px solid #000; padding-bottom: 14px; margin-bottom: 18px; }
  .brand { font-family: 'Georgia', serif; font-size: 26px; font-weight: bold; }
  .sub { font-size: 10px; letter-spacing: 0.2em; text-transform: uppercase; color: #555; }
  h3 { font-size: 12px; text-transform: uppercase; letter-spacing: 0.1em; margin: 18px 0 6px; }
  table { width: 100%; border-collapse: collapse; }
  th { background: #18181b; color: #fff; text-align: left; padding: 6px 8px; font-size: 10px; text-transform: uppercase; }
  td { padding: 6px 8px; border-bottom: 1px solid #e4e4e7; font-size: 11px; }
  .totals { width: 300px; margin-left: auto; margin-top: 14px; }
  .totals td { font-size: 12px; }
  .totals .grand td { border-top: 2px solid #000; font-size: 16px; font-weight: bold; padding-top: 8px; color: #b91c1c; }
  .foot { margin-top: 30px; border-top: 1px solid #ccc; padding-top: 8px; font-size: 10px; color: #555; text-align: center; }
  .actions { margin-top: 24px; display: flex; gap: 10px; justify-content: center; }
  .btn { padding: 10px 24px; border: 1px solid #000; background: #fff; cursor: pointer; font-size: 12px; text-transform: uppercase; }
  .btn-primary { background: #000; color: #fff; }
  @media print { .actions { display: none; } .doc { padding: 0; } }
</style></head>
<body>
<div class="doc">
  <div class="head">
    <div style="display:flex;align-items:center;gap:14px;">
      <img src="${LOGO_URL}" style="height:56px;object-fit:contain;" onerror="this.style.display='none'"/>
      <div>
        <div class="brand">${escapeHtml(businessName)}</div>
        <div class="sub">Supplier Statement · ${escapeHtml(catLabel)}</div>
      </div>
    </div>
    <div style="text-align:right">
      <div style="font-size:14px;font-weight:bold;">${escapeHtml(ledger.supplier)}</div>
      <div style="font-size:11px;color:#444;">As of ${escapeHtml(fmtDate(new Date().toISOString().slice(0, 10)))}</div>
    </div>
  </div>

  <h3>Purchases (${purchases.length})</h3>
  <table>
    <thead><tr><th>Date</th><th>Type / Item</th><th style="text-align:right;">Qty</th><th style="text-align:right;">Rate/kg</th><th style="text-align:right;">Amount</th><th style="text-align:right;">Paid</th><th style="text-align:center;">Status</th></tr></thead>
    <tbody>${rows || '<tr><td colspan="7" style="text-align:center;color:#777;">No purchases in this category</td></tr>'}</tbody>
  </table>

  ${payRows ? `<h3>Payments Made</h3>
  <table>
    <thead><tr><th>Date</th><th>Mode</th><th>Notes</th><th style="text-align:right;">Amount</th></tr></thead>
    <tbody>${payRows}</tbody>
  </table>` : ""}

  <table class="totals">
    <tr><td>Total Billed</td><td style="text-align:right;font-family:monospace;">${fmtINR(billed)}</td></tr>
    <tr><td>Paid on Invoice</td><td style="text-align:right;font-family:monospace;">− ${fmtINR(paid)}</td></tr>
    ${category === "all" && extraPaid > 0 ? `<tr><td>Other Payments</td><td style="text-align:right;font-family:monospace;">− ${fmtINR(extraPaid)}</td></tr>` : ""}
    <tr class="grand"><td>BALANCE DUE</td><td style="text-align:right;font-family:monospace;">${fmtINR(Math.max(0, balance))}</td></tr>
  </table>

  <div class="foot">${escapeHtml(businessName)} · ${escapeHtml(companyPhone)}</div>
  <div class="actions">
    <button class="btn" onclick="window.close()">Close</button>
    <button class="btn btn-primary" onclick="window.print()">Print</button>
  </div>
</div>
</body></html>`;

  const w = window.open("", "_blank", "width=820,height=800");
  if (!w) return;
  w.document.write(html);
  w.document.close();
}

/* ---------- Advance Booking Receipt (party orders without rate) ---------- */
export function openBookingWindow(order, { businessName = "EEMAN MEATS", companyPhone = "9025811576" } = {}) {
  const rows = (order.items || [])
    .map(
      (it) => `<tr>
        <td>${escapeHtml(it.product_name)}</td>
        <td style="text-align:right;font-weight:bold;">${(it.unit || "kg") === "piece" ? Math.round(it.kg) + " pcs" : fmtKg(it.kg) + " kg"}</td>
        <td style="text-align:right;color:#777;">${it.rate > 0 ? "₹" + fmtKg(it.rate) : "To be confirmed"}</td>
      </tr>`
    )
    .join("");

  const html = `<!doctype html>
<html><head><meta charset="utf-8"/>
<title>BOOKING #${order.order_no} — ${escapeHtml(order.customer_name)}</title>
<style>
  @page { size: A4; margin: 14mm; }
  body { font-family: 'Helvetica', 'Arial', sans-serif; margin: 0; color: #000; font-size: 12px; line-height: 1.5; }
  .doc { max-width: 210mm; margin: 0 auto; padding: 20px; }
  .head { display: flex; justify-content: space-between; align-items: flex-start; border-bottom: 3px solid #000; padding-bottom: 14px; margin-bottom: 18px; }
  .brand { font-family: 'Georgia', serif; font-size: 26px; font-weight: bold; }
  .sub { font-size: 10px; letter-spacing: 0.2em; text-transform: uppercase; color: #b45309; font-weight: bold; }
  table { width: 100%; border-collapse: collapse; margin-top: 8px; }
  th { background: #18181b; color: #fff; text-align: left; padding: 6px 8px; font-size: 10px; text-transform: uppercase; }
  td { padding: 8px; border-bottom: 1px solid #e4e4e7; font-size: 12px; }
  .meta { display: flex; gap: 24px; flex-wrap: wrap; margin-bottom: 8px; font-size: 12px; }
  .meta b { display: block; font-size: 10px; text-transform: uppercase; color: #555; }
  .advance { margin-top: 16px; border: 2px solid #059669; background: #ecfdf5; padding: 12px 16px; display: flex; justify-content: space-between; align-items: center; }
  .advance .amt { font-size: 22px; font-weight: bold; font-family: monospace; color: #047857; }
  .note { margin-top: 12px; font-size: 10px; color: #92400e; background: #fffbeb; border: 1px solid #fde68a; padding: 8px 12px; }
  .foot { margin-top: 30px; border-top: 1px solid #ccc; padding-top: 8px; font-size: 10px; color: #555; text-align: center; }
  .actions { margin-top: 24px; display: flex; gap: 10px; justify-content: center; }
  .btn { padding: 10px 24px; border: 1px solid #000; background: #fff; cursor: pointer; font-size: 12px; text-transform: uppercase; }
  .btn-primary { background: #000; color: #fff; }
  @media print { .actions { display: none; } .doc { padding: 0; } }
</style></head>
<body>
<div class="doc">
  <div class="head">
    <div style="display:flex;align-items:center;gap:14px;">
      <img src="${LOGO_URL}" style="height:56px;object-fit:contain;" onerror="this.style.display='none'"/>
      <div>
        <div class="brand">${escapeHtml(businessName)}</div>
        <div class="sub">Advance Booking Receipt</div>
      </div>
    </div>
    <div style="text-align:right">
      <div style="font-size:18px;font-weight:bold;">Booking #${order.order_no}</div>
    </div>
  </div>

  <div class="meta">
    <div><b>Customer</b>${escapeHtml(order.customer_name)}${order.phone ? `<br/><span style="color:#555;">${escapeHtml(order.phone)}</span>` : ""}</div>
    <div><b>Bill Date</b>${escapeHtml(fmtDate(order.date))}</div>
    ${order.delivery_date ? `<div><b>Delivery Date</b>${escapeHtml(fmtDate(order.delivery_date))}</div>` : ""}
  </div>

  <table>
    <thead><tr><th>Product</th><th style="text-align:right;">Ordered Qty</th><th style="text-align:right;">Rate</th></tr></thead>
    <tbody>${rows}</tbody>
  </table>

  <div class="advance">
    <span style="font-size:12px;text-transform:uppercase;letter-spacing:0.1em;font-weight:bold;">Advance Collected</span>
    <span class="amt">${fmtINR(order.paid_amount || 0)}</span>
  </div>

  <div class="note">Rate &amp; final amount will be confirmed at the time of delivery. Final bill will be issued against this booking number. Ordered quantity can be revised on request.</div>

  <div class="foot">${escapeHtml(businessName)} · ${escapeHtml(companyPhone)} — Thank you for your booking</div>
  <div class="actions">
    <button class="btn" onclick="window.close()">Close</button>
    <button class="btn btn-primary" onclick="window.print()">Print</button>
  </div>
</div>
</body></html>`;

  const w = window.open("", "_blank", "width=820,height=800");
  if (!w) return;
  w.document.write(html);
  w.document.close();
}

export function shareBookingWhatsApp(order, { phone = "", businessName = "EEMAN MEATS", companyPhone = "9025811576" } = {}) {
  const lines = [
    `*${businessName}* — ADVANCE BOOKING #${order.order_no}`,
    `Customer: ${order.customer_name}`,
    `Bill Date: ${fmtDate(order.date)}`,
    order.delivery_date ? `Delivery: ${fmtDate(order.delivery_date)}` : "",
    "",
    "Ordered:",
    ...(order.items || []).map(
      (it) => `• ${it.product_name} — ${(it.unit || "kg") === "piece" ? Math.round(it.kg) + " pcs" : fmtKg(it.kg) + " kg"}`
    ),
    "",
    `*Advance Collected: ${fmtINR(order.paid_amount || 0)}*`,
    "Rate & final bill will be confirmed on delivery.",
    "",
    `${businessName} · ${companyPhone}`,
    "Thank you 🙏",
  ]
    .filter(Boolean)
    .join("\n");
  const text = encodeURIComponent(lines);
  const url = phone
    ? `https://wa.me/${phone.replace(/[^\d]/g, "")}?text=${text}`
    : `https://wa.me/?text=${text}`;
  window.open(url, "_blank");
}
